import React, { useRef, Component } from 'react';
export const MasterclassCurriculum = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const masterclasses = [{
    month: 'DEC',
    date: 'December 3, 2025',
    time: '12:00 PM ET / 9:00 AM PT',
    title: 'High-Margin Destination Wedding Economics',
    hook: 'Turn every wedding into a multi-revenue ladder: planning fees → room-night commissions → on-site upsells.',
    keyLearnings: ['The 5 levers of margin in destination weddings', 'Commission stacking ethically with hotels/resorts and DMCs', 'Pricing the planner premium for complexity and risk'],
    deliverables: 'Margin Map template, Destination Pricing Calculator, Multi-tier package menu',
    calendlyLink: 'https://calendly.com/ashsingh/high-margin-destination-wedding-economics'
  }, {
    month: 'JAN',
    date: 'January 7, 2026',
    time: '12:00 PM ET / 9:00 AM PT',
    title: 'Inbox-to-Deposit: The Conversion Engine',
    hook: 'Stop "proposal purgatory"—engineer same-day decisions.',
    keyLearnings: ['WhatsApp-first sales flow, discovery script, and urgency triggers', 'Proposal structure that sells (3 options, anchored pricing, timeline)', 'Calendly + payment link stack for frictionless deposits'],
    deliverables: 'Discovery Call Script, 3-Option Proposal Deck, Deposit-in-Call checklist',
    calendlyLink: 'https://calendly.com/ashsingh/inbox-to-deposit-the-conversion-engine'
  }, {
    month: 'FEB',
    date: 'February 4, 2026',
    time: '12:00 PM ET / 9:00 AM PT',
    title: 'Planner P&L & Cash Flow Mastery',
    hook: 'Pay yourself first, fund growth, and sleep at night.',
    keyLearnings: ['Retainer vs. milestone billing that matches cash burn', 'Late-fee & interest policy that actually gets paid', 'Forecasting taxes, travel floats, and team bonuses'],
    deliverables: 'Planner P&L model, Cash-Flow Forecaster, Late-Fee clause library',
    calendlyLink: 'https://calendly.com/ashsingh/planner-p-and-l-cash-flow-mastery'
  }, {
    month: 'MAR',
    date: 'March 4, 2026',
    time: '12:00 PM ET / 9:00 AM PT',
    title: 'Room Blocks, Attrition & Comp-Night Alchemy',
    hook: 'Make room blocks your predictable profit center.',
    keyLearnings: ['Negotiating blocks, upgrades, and comps in all-inclusive resorts', 'Attrition risk, release dates, and audit tactics', 'Transparent guest comms that fill the block fast'],
    deliverables: 'Room-Block Negotiation Checklist, Attrition Scenario Tool, Guest FAQ email pack',
    calendlyLink: 'https://calendly.com/ashsingh/room-blocks-attrition-comp-night-alchemy'
  }, {
    month: 'APR',
    date: 'April 1, 2026',
    time: '12:00 PM ET / 9:00 AM PT',
    title: 'Vendor Contracts, Risk & "What If" Scenarios',
    hook: 'Lock in excellence; limit downside.',
    keyLearnings: ['SLAs that tie payment to performance', 'Force majeure, weather, and contingency playbooks', 'Insurance, permits, and cross-border compliance'],
    deliverables: 'Contract Clause Library, Risk Matrix, Event Day Contingency Kit',
    calendlyLink: 'https://calendly.com/ashsingh/vendor-contracts-risk-and-what-if-scenarios'
  }, {
    month: 'MAY',
    date: 'May 6, 2026',
    time: '12:00 PM ET / 9:00 AM PT',
    title: 'Guest Experience & On-Site Upsell Playbook',
    hook: 'Delight guests and add $300–$800 per head in value.',
    keyLearnings: ['Designing sellable micro-moments (spa, golf, excursions, private tastings)', '"Shop the Wedding" merch: welcome kits, luggage tags, keepsakes', 'WhatsApp concierge flows for impulse buys on-site'],
    deliverables: 'Upsell Menu & Pricing Guide, WhatsApp Concierge Scripts, Vendor revenue-share templates',
    calendlyLink: 'https://calendly.com/ashsingh/guest-experience-on-site-upsell-playbook'
  }, {
    month: 'JUN',
    date: 'June 3, 2026',
    time: '12:00 PM ET / 9:00 AM PT',
    title: 'Content That Sells: UGC, Reels & Influencer Collabs',
    hook: 'Turn one wedding into 30 days of pipeline.',
    keyLearnings: ['On-site content roles (shot list, rights, approvals)', 'Creator agreements and attribution', 'The 5-post launch ladder (teaser → BTS → hero → testimonials → offer)'],
    deliverables: 'Reels Shot List, Creator Agreement template, 30-Day Content Calendar',
    calendlyLink: 'https://calendly.com/ashsingh/content-that-sells-ugc-reels-influencer-collabs'
  }, {
    month: 'JUL',
    date: 'July 8, 2026',
    time: '12:00 PM ET / 9:00 AM PT',
    title: 'AI Ops for Planners: SOPs, RACI & Automations',
    hook: 'Save 10+ hours/week without losing the human touch.',
    keyLearnings: ['RACI mapping for your studio (who owns what, and when)', 'SOPs for briefs, approvals, timelines; AI drafting for emails, run-of-show, checklists', 'Automations: lead capture → CRM → tasking → reminders'],
    deliverables: 'RACI Template, 12 Plug-and-Play SOPs, Automation Recipes',
    calendlyLink: 'https://calendly.com/ashsingh/ai-ops-for-planners-sops-raci-automations'
  }, {
    month: 'AUG',
    date: 'August 5, 2026',
    time: '12:00 PM ET / 9:00 AM PT',
    title: 'Crisis Comms, Refunds & Reputation Armor',
    hook: 'When things wobble, you rise—publicly and profitably.',
    keyLearnings: ['Triage tree (client, guests, vendors, venue, media)', 'Refund/credit frameworks that protect margin', 'Review strategy: capture raves, contain rants'],
    deliverables: 'Crisis Scripts Pack, Refund/Credit Matrix, Review Response Library',
    calendlyLink: 'https://calendly.com/ashsingh/crisis-comms-refunds-reputation-armor'
  }, {
    month: 'SEP',
    date: 'September 2, 2026',
    time: '12:00 PM ET / 9:00 AM PT',
    title: 'Luxury Positioning & Premium Pricing',
    hook: 'Move from "planner" to "house of taste."',
    keyLearnings: ['Signature aesthetic & sensory codes (menu, music, materials)', 'Tiered offers and scarcity mechanics', 'Website & deck polish that commands premium rates'],
    deliverables: 'Luxury Positioning Guide, Offer Ladder Template, Sales Deck skeleton',
    calendlyLink: 'https://calendly.com/ashsingh/luxury-positioning-premium-pricing'
  }, {
    month: 'OCT',
    date: 'October 7, 2026',
    time: '12:00 PM ET / 9:00 AM PT',
    title: 'Beyond Weddings: Corporate Retreats & MICE',
    hook: 'Monetize your off-season with corporate dollars.',
    keyLearnings: ['Translating your destination chops to offsites and incentives', 'Outreach to HR/L&D/ops buyers; procurement basics', 'Bundling learning + leisure (boardroom-to-beach)'],
    deliverables: 'Retreat Proposal Pack, Buyer Persona Briefs, Outreach Email/LinkedIn scripts',
    calendlyLink: 'https://calendly.com/ashsingh/beyond-weddings-corporate-retreats-mice'
  }, {
    month: 'NOV',
    date: 'November 4, 2026',
    time: '12:00 PM ET / 9:00 AM PT',
    title: 'Deal-Making Sprint & 90-Day Booking Plan',
    hook: 'Finish the year with signatures; start Q1 hot.',
    keyLearnings: ['Partnership stacking (venues, resorts, chefs, creators, travel agents)', 'Flash offers and booking sprints', 'KPI cadence and weekly pipeline rhythm'],
    deliverables: '90-Day Booking Planner, Partnership MOU templates, KPI Scorecard',
    calendlyLink: 'https://calendly.com/ashsingh/deal-making-sprint-90-day-booking-plan'
  }];
  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  return <section id="masterclass-curriculum" className="py-16 md:py-24 bg-gradient-to-b from-white to-limestone-50 relative overflow-hidden" data-id="element-411">
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1540575467063-178a50c2df87?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')] opacity-5 bg-fixed" data-id="element-412"></div>
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-413">
        <div className="text-center mb-10 md:mb-12" data-id="element-414">
          <span className="inline-block px-4 py-1 bg-limestone-700 text-white rounded-full text-sm font-medium mb-4" data-id="element-415">
            EXCLUSIVE MEMBER EDUCATION
          </span>
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-4 md:mb-6 text-gray-800 tracking-tight" data-id="element-416">
            12-Month Masterclass Curriculum
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-6" data-id="element-417">
            Interactive, implementation-first sessions hosted by Professor Ash
            Singh
          </p>
          <div className="inline-flex items-center gap-2 bg-limestone-700 text-white px-6 py-3 rounded-lg shadow-lg mb-4" data-id="element-418">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-419">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" data-id="element-420"></path>
              <circle cx="12" cy="10" r="3" data-id="element-421"></circle>
            </svg>
            <span className="font-bold text-lg" data-id="element-422">
              Win 2 Nights in Cancun Every Session!
            </span>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-2xl mx-auto border-2 border-limestone-200" data-id="element-423">
            <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center justify-center gap-2" data-id="element-424">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-limestone-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-425">
                <circle cx="12" cy="12" r="10" data-id="element-426"></circle>
                <line x1="12" y1="16" x2="12" y2="12" data-id="element-427"></line>
                <line x1="12" y1="8" x2="12.01" y2="8" data-id="element-428"></line>
              </svg>
              How to Win Your Cancun Getaway
            </h3>
            <div className="text-left space-y-3 text-sm text-gray-600" data-id="element-429">
              <p className="font-medium text-limestone-800" data-id="element-430">
                The member who brings the most colleagues to the session will
                win a trip to a luxury resort in Cancun, Mexico!
              </p>
              <p className="italic" data-id="element-431">
                (Please note: your colleagues must RSVP and attend the session
                to qualify.)
              </p>
              <p className="text-gray-600" data-id="element-432">
                Please ensure the colleagues you bring are wedding planners
                currently working on destination weddings in Mexico or the
                Caribbean, or those who have the capacity and interest to take
                on destination events in these regions.
              </p>
            </div>
            <div className="mt-6 pt-6 border-t border-limestone-200" data-id="element-433">
              <p className="text-center text-sm font-medium text-gray-700 mb-3" data-id="element-434">
                Share this opportunity with your network:
              </p>
              <div className="mb-4 bg-limestone-50 rounded-lg p-4 border border-limestone-200" data-id="element-435">
                <label className="block text-xs font-medium text-gray-600 mb-2" data-id="element-436">
                  Copy & Share:
                </label>
                <div className="relative" data-id="element-437">
                  <textarea readOnly value="Win a trip to Cancun! Check out the Elite Planners Association masterclass series: https://eliteplannersassociation.com/" className="w-full p-3 pr-20 text-sm text-gray-700 bg-white border border-limestone-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-limestone-700" rows={3} onClick={e => e.currentTarget.select()} data-id="element-438" />
                  <button onClick={e => {
                  const textarea = e.currentTarget.previousElementSibling as HTMLTextAreaElement;
                  textarea.select();
                  navigator.clipboard.writeText(textarea.value);
                  const button = e.currentTarget;
                  const originalText = button.innerHTML;
                  button.innerHTML = '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg><span>Copied!</span>';
                  setTimeout(() => {
                    button.innerHTML = originalText;
                  }, 2000);
                }} className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 px-3 py-1.5 bg-limestone-700 hover:bg-limestone-800 text-white text-xs rounded transition-colors" data-id="element-439">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-440">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" data-id="element-441" />
                    </svg>
                    Copy
                  </button>
                </div>
              </div>
              <div className="flex justify-center gap-3 flex-wrap" data-id="element-442">
                <a href={`https://wa.me/?text=${encodeURIComponent('Win a trip to Cancun! Check out the Elite Planners Association masterclass series: https://eliteplannersassociation.com/')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-[#25D366] hover:bg-[#1DA851] text-white rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md text-sm font-medium" data-id="element-443">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" data-id="element-444">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" data-id="element-445" />
                  </svg>
                  WhatsApp
                </a>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent('https://eliteplannersassociation.com/')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-[#1877F2] hover:bg-[#166FE5] text-white rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md text-sm font-medium" data-id="element-446">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" data-id="element-447">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" data-id="element-448" />
                  </svg>
                  Facebook
                </a>
                <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent('https://eliteplannersassociation.com/')}&text=${encodeURIComponent('Win a trip to Cancun! Join the Elite Planners Association masterclass series.')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-black hover:bg-gray-800 text-white rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md text-sm font-medium" data-id="element-449">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" data-id="element-450">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" data-id="element-451" />
                  </svg>
                  X
                </a>
                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent('https://eliteplannersassociation.com/')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-[#0A66C2] hover:bg-[#004182] text-white rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md text-sm font-medium" data-id="element-452">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" data-id="element-453">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" data-id="element-454" />
                  </svg>
                  LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="relative" data-id="element-455">
          <button onClick={() => scroll('left')} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white p-3 rounded-full shadow-xl transition-all duration-300 hover:scale-110 hidden md:block" aria-label="Scroll left" data-id="element-456">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-limestone-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-457">
              <polyline points="15 18 9 12 15 6" data-id="element-458"></polyline>
            </svg>
          </button>
          <div ref={scrollContainerRef} className="flex gap-6 overflow-x-auto pb-6 snap-x snap-mandatory scrollbar-hide" style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }} data-id="element-459">
            {masterclasses.map((masterclass, index) => <div key={index} className="flex-shrink-0 w-[340px] md:w-[400px] snap-center" data-id="element-460">
                <div className="bg-white rounded-xl shadow-xl overflow-hidden h-full transform transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl border border-limestone-200" data-id="element-461">
                  <div className="bg-limestone-700 text-white p-6" data-id="element-462">
                    <div className="flex items-center justify-between mb-3" data-id="element-463">
                      <span className="text-3xl font-bold font-serif" data-id="element-464">
                        {masterclass.month}
                      </span>
                      <span className="px-3 py-1 bg-white/20 rounded-full text-sm" data-id="element-465">
                        Session {index + 1}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold font-serif leading-tight mb-3" data-id="element-466">
                      {masterclass.title}
                    </h3>
                    <div className="text-sm text-limestone-100 space-y-1" data-id="element-467">
                      <div className="flex items-center gap-2" data-id="element-468">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-469">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" data-id="element-470"></rect>
                          <line x1="16" y1="2" x2="16" y2="6" data-id="element-471"></line>
                          <line x1="8" y1="2" x2="8" y2="6" data-id="element-472"></line>
                          <line x1="3" y1="10" x2="21" y2="10" data-id="element-473"></line>
                        </svg>
                        <span data-id="element-474">{masterclass.date}</span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-475">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-476">
                          <circle cx="12" cy="12" r="10" data-id="element-477"></circle>
                          <polyline points="12 6 12 12 16 14" data-id="element-478"></polyline>
                        </svg>
                        <span data-id="element-479">{masterclass.time}</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-6" data-id="element-480">
                    <div className="mb-4" data-id="element-481">
                      <div className="flex items-start gap-2 mb-3" data-id="element-482">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-limestone-700 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-483">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" data-id="element-484"></path>
                          <polyline points="22 4 12 14.01 9 11.01" data-id="element-485"></polyline>
                        </svg>
                        <p className="text-limestone-800 font-medium italic" data-id="element-486">
                          {masterclass.hook}
                        </p>
                      </div>
                    </div>
                    <div className="mb-4" data-id="element-487">
                      <h4 className="font-bold text-gray-800 mb-2 text-sm uppercase tracking-wide" data-id="element-488">
                        You'll Learn:
                      </h4>
                      <ul className="space-y-2" data-id="element-489">
                        {masterclass.keyLearnings.map((learning, idx) => <li key={idx} className="flex items-start gap-2 text-sm" data-id="element-490">
                            <span className="inline-block w-1.5 h-1.5 bg-limestone-700 rounded-full mt-1.5 flex-shrink-0" data-id="element-491"></span>
                            <span className="text-gray-600" data-id="element-492">{learning}</span>
                          </li>)}
                      </ul>
                    </div>
                    <div className="pt-4 border-t border-limestone-100 mb-4" data-id="element-493">
                      <h4 className="font-bold text-gray-800 mb-2 text-sm uppercase tracking-wide" data-id="element-494">
                        Take-Home Assets:
                      </h4>
                      <p className="text-sm text-gray-600" data-id="element-495">
                        {masterclass.deliverables}
                      </p>
                    </div>
                    <a href={masterclass.calendlyLink} target="_blank" rel="noopener noreferrer" className="w-full bg-limestone-700 hover:bg-limestone-800 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg flex items-center justify-center gap-2" data-id="element-496">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-497">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" data-id="element-498"></path>
                        <circle cx="8.5" cy="7" r="4" data-id="element-499"></circle>
                        <line x1="20" y1="8" x2="20" y2="14" data-id="element-500"></line>
                        <line x1="23" y1="11" x2="17" y2="11" data-id="element-501"></line>
                      </svg>
                      Register Now
                    </a>
                  </div>
                  <div className="bg-limestone-50 px-6 py-4 border-t border-limestone-200" data-id="element-502">
                    <div className="flex items-center justify-between text-sm" data-id="element-503">
                      <span className="text-gray-600 flex items-center gap-1" data-id="element-504">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-505">
                          <circle cx="12" cy="12" r="10" data-id="element-506"></circle>
                          <polyline points="12 6 12 12 16 14" data-id="element-507"></polyline>
                        </svg>
                        90 minutes
                      </span>
                      <span className="text-limestone-700 font-medium" data-id="element-508">
                        Interactive Workshop
                      </span>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
          <button onClick={() => scroll('right')} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white p-3 rounded-full shadow-xl transition-all duration-300 hover:scale-110 hidden md:block" aria-label="Scroll right" data-id="element-509">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-limestone-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-510">
              <polyline points="9 18 15 12 9 6" data-id="element-511"></polyline>
            </svg>
          </button>
        </div>
        <div className="mt-12 text-center" data-id="element-512">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-3xl mx-auto border-2 border-limestone-200" data-id="element-513">
            <h3 className="text-2xl font-serif font-bold text-gray-800 mb-4" data-id="element-514">
              Program Details
            </h3>
            <div className="grid md:grid-cols-2 gap-6 text-left" data-id="element-515">
              <div data-id="element-516">
                <h4 className="font-bold text-limestone-700 mb-2" data-id="element-517">Format</h4>
                <p className="text-gray-600 text-sm" data-id="element-518">Monthly sessions</p>
              </div>
              <div data-id="element-519">
                <h4 className="font-bold text-limestone-700 mb-2" data-id="element-520">
                  Cohort Size
                </h4>
                <p className="text-gray-600 text-sm" data-id="element-521">
                  25 to 60 planners for intimate Q&A and collaboration
                </p>
                <p className="text-gray-500 text-xs mt-1 italic" data-id="element-522">
                  Note: Sessions require a minimum of 25 RSVPs and will be
                  cancelled if this threshold is not met.
                </p>
              </div>
              <div data-id="element-523">
                <h4 className="font-bold text-limestone-700 mb-2" data-id="element-524">Access</h4>
                <p className="text-gray-600 text-sm" data-id="element-525">
                  48-hour replay + permanent access to all templates and
                  checklists
                </p>
              </div>
              <div data-id="element-526">
                <h4 className="font-bold text-limestone-700 mb-2" data-id="element-527">
                  Certification
                </h4>
                <p className="text-gray-600 text-sm" data-id="element-528">
                  EPA Certified badge after completing 6+ sessions
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};